import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import ParkingIcon from 'mdi-react/ParkingIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import FormatListBulletedIcon from 'mdi-react/FormatListBulletedIcon';
import ViewGridOutlineIcon from 'mdi-react/ViewGridOutlineIcon';
import FilterRemoveOutlineIcon from 'mdi-react/FilterRemoveOutlineIcon';
import ActivityIndicator
  from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center }
  from '../../../core/components/center/center.component';
import ListPager
  from '../../../core/components/list-pager/list-pager.component';
import notify
  from '../../../core/components/notify/notify.component';
import { IsMobileContext }
  from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import stringIsNullOrWhitespace
  from '../../../core/utils/validation-utils';
import type Office from '../../offices/models/office';
import ParkingCard from '../cards/parking-card.component';
import CreateParkingModal
  from '../modals/create-parking-modal.component';
import type Parking from '../models/parking';
import {
  createParking,
  updateParking,
  deleteParkingById,
} from '../services/parkings.service';
import * as listStyles from '../../../core/admin-list.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import { useSessionState } from '../../../core/hooks/session-state.hook';
import { useKeycloak }
  from '../../authentication/keycloak/KeycloakProvider';

const ALL_OFFICES_VALUE = '__all__';
// Areas nobody can book because they hang off no office — worth being able to isolate.
const NOT_LINKED_VALUE = '__not_linked__';
const ITEMS_PER_PAGE = 10;

/** Session-storage keys for this tab's toolbar. Namespaced by tab so the three do not share. */
const FILTER_KEYS = {
  search: 'my-office.parkings.search',
  office: 'my-office.parkings.office',
  sort: 'my-office.parkings.sort',
  view: 'my-office.parkings.view',
};

type ParkingSort = 'name' | 'capacity' | 'unlinked';
type ParkingView = 'list' | 'grid';

const SORT_OPTIONS: { value: ParkingSort; labelKey: string }[] = [
  { value: 'name', labelKey: 'sort-name' },
  { value: 'capacity', labelKey: 'sort-largest-capacity' },
  { value: 'unlinked', labelKey: 'sort-unlinked-first' },
];

interface Props {
  readonly allOffices: Office[] | undefined;
  readonly allParkings: Parking[] | undefined;
  readonly initialized: boolean;
}

const OfficeParkingsGroup = (
  { allOffices, allParkings, initialized }: Props,
): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [showCreateParkingModal, setShowCreateParkingModal] = (
    useState(false)
  );
  const [isLoading, setIsLoading] = useState(true);
  const keycloak = useKeycloak();
  const [parkings, setParkings] = useState(allParkings);
  // Held in session storage so a reload does not throw away the filters the admin set up.
  // Page is deliberately not persisted: the list it indexes into can change between visits.
  const [searchQuery, setSearchQuery] = useSessionState(FILTER_KEYS.search, '');
  const [selectedOffice, setSelectedOffice] = useSessionState(
    FILTER_KEYS.office,
    ALL_OFFICES_VALUE,
  );
  const [sortOrder, setSortOrder] = useSessionState<ParkingSort>(FILTER_KEYS.sort, 'name');
  const [view, setView] = useSessionState<ParkingView>(FILTER_KEYS.view, 'list');
  const [page, setPage] = useState(0);

  // Sort order and the list/grid switch are display preferences rather than filters: they
  // hide nothing, so clearing does not touch them.
  const hasActiveFilters = searchQuery.trim() !== '' || selectedOffice !== ALL_OFFICES_VALUE;

  const clearFilters = useCallback((): void => {
    setSearchQuery('');
    setSelectedOffice(ALL_OFFICES_VALUE);
    setPage(0);
  }, [setSearchQuery, setSelectedOffice]);

  /**
   * Synchronise the local `parkings` copy and clear the loading state once the
   * parent signals that the fetch has completed (`initialized === true`).
   *
   * This replaces the previous pattern of calling `setParkings` / `setIsLoading`
   * directly in the render body.  Calling setState during render is a React
   * anti-pattern: React schedules an extra synchronous re-render, meaning the
   * spinner stays visible for an extra paint cycle after data arrives.  Moving
   * the side-effect into `useEffect` eliminates that extra cycle and makes the
   * transition from spinner → content happen in a single render pass.
   */
  useEffect(() => {
    if (initialized) {
      setParkings(allParkings);
      setIsLoading(false);
    }
  // `allParkings` is intentionally included so that if the parent re-fetches
  // (e.g. after a cache invalidation) the local copy is refreshed accordingly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, allParkings]);

  const sortedParkings = useMemo(
    () => [...(parkings ?? [])].sort(
      (a, b) => (
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      ),
    ),
    [parkings],
  );

  const filteredParkings = useMemo(() => {
    let result = sortedParkings;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) => p.name.toLowerCase().includes(q),
      );
    }
    if (selectedOffice === NOT_LINKED_VALUE) {
      result = result.filter((p) => p.officeIds.length === 0);
    } else if (selectedOffice !== ALL_OFFICES_VALUE) {
      result = result.filter(
        (p) => p.officeIds.includes(selectedOffice),
      );
    }

    // `sortedParkings` is already name-ordered, so the other orders only need their own key with
    // the name as the tie-breaker.
    if (sortOrder === 'capacity') {
      result = [...result].sort((a, b) => (
        Number(b.capacity) - Number(a.capacity)
        || (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
      ));
    } else if (sortOrder === 'unlinked') {
      result = [...result].sort((a, b) => (
        a.officeIds.length - b.officeIds.length
        || (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
      ));
    }

    return result;
  }, [sortedParkings, searchQuery, selectedOffice, sortOrder]);

  // Deleting the last item on the last page shrinks the list under the current page; without
  // clamping the panel renders an empty slice and the pager hides itself, stranding the reader.
  const lastPage = Math.max(0, Math.ceil(filteredParkings.length / ITEMS_PER_PAGE) - 1);
  const currentPage = Math.min(page, lastPage);

  const paginatedParkings = filteredParkings.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  // How many areas exist that nobody can reach, called out under the title.
  const unlinkedCount = sortedParkings.filter(
    (parking) => parking.officeIds.length === 0,
  ).length;

  const handleShowCreate = (): void => {
    setShowCreateParkingModal(true);
  };
  const handleClose = (): void => {
    setShowCreateParkingModal(false);
  };

  const parkingIsValid = (parking: Parking): boolean => {
    if (stringIsNullOrWhitespace(parking.name)) {
      notify(t('name-empty-message'), false);
      return false;
    }
    if (Number(parking.capacity) <= 0) {
      notify(t('zero-parking-spots-added-error'), false);
      return false;
    }
    return true;
  };

  const shouldCreateParking = async (
    parkingToCreate: Parking,
  ): Promise<void> => {
    const parkingResult = await createParking(
      parkingToCreate, keycloak,
    );
    if (parkingResult != null) {
      parkings?.push(parkingResult);
      setParkings(parkings ?? []);
      handleClose();
    } else if (!parkingResult) {
      notify(t('create-parking-error-message'), false);
    }
  };

  const shouldUpdateParking = async (
    originalParking: Parking,
    updatedParking: Parking,
  ): Promise<void> => {
    const parkingResult = await updateParking(
      originalParking, updatedParking, keycloak,
    );
    if (parkingResult != null) {
      const existing = parkings?.filter(
        (parking) => parking.id !== parkingResult.id,
      );
      existing?.push(parkingResult);
      setParkings(existing);
      handleClose();
    } else if (!parkingResult) {
      notify(t('edit-parking-error-message'), false);
    }
  };

  const shouldDeleteParking = async (
    parkingtoDelete: Parking,
  ): Promise<void> => {
    if (parkingtoDelete.id != null) {
      await deleteParkingById(parkingtoDelete.id, keycloak);
      const remaining = parkings?.filter(
        (parking) => parking.id !== parkingtoDelete.id,
      );
      setParkings(remaining);
      handleClose();
    } else {
      notify(t('delete-parking-error-message'), false);
    }
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleOfficeFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    setSelectedOffice(e.target.value);
    setPage(0);
  };

  const showSpinner = isLoading || !initialized || allOffices == null;

  const renderSectionHead = (): React.JSX.Element => (
    <div className={style(surfaceStyles.cardHead(isMobile))}>
      <div className={style(listStyles.panelHeadLeft)}>
        <div className={style(listStyles.sectionTile)}>
          <ParkingIcon size={20} />
        </div>
        <div>
          <h2 className={style(listStyles.panelTitle)}>
            {t('parkings')}
            {!showSpinner && (
              <span className={style(listStyles.countPill)}>
                {sortedParkings.length}
              </span>
            )}
          </h2>
          {!showSpinner && unlinkedCount > 0 && (
            <span className={style(listStyles.warningLine)}>
              {(t('parkings-not-linked-warning') ?? '').replace('[x]', unlinkedCount.toString())}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        className={style(surfaceStyles.primaryButton)}
        onClick={handleShowCreate}
        disabled={isLoading}
      >
        <PlusIcon size={16} />
        {t('create-new-parking')}
      </button>
    </div>
  );

  const renderToolbar = (): React.JSX.Element | null => {
    if (showSpinner || sortedParkings.length === 0) return null;
    return (
      <div className={style(listStyles.toolbar(isMobile))}>
        <div className={style(surfaceStyles.searchWrap(isMobile))}>
          <span className={style(surfaceStyles.searchIcon)}>
            <MagnifyIcon size={16} />
          </span>
          <input
            type="text"
            className={style(surfaceStyles.searchInput)}
            placeholder={String(t('search-by-name'))}
            aria-label={String(t('search-by-name'))}
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        <select
          className={style(listStyles.filterSelect(isMobile))}
          value={selectedOffice}
          aria-label={String(t('all-offices'))}
          onChange={handleOfficeFilterChange}
        >
          <option value={ALL_OFFICES_VALUE}>{t('all-offices')}</option>
          <option value={NOT_LINKED_VALUE}>{t('not-linked')}</option>
          {[...(allOffices ?? [])]
            .sort((a, b) => (
              a.name.toLowerCase() > b.name.toLowerCase()
                ? 1 : -1
            ))
            .map((office) => (
              <option key={office.id} value={office.id ?? ''}>
                {office.name}
              </option>
            ))}
        </select>

        {/* Sort and the view switch sit at the far end of the toolbar. */}
        <select
          className={style(listStyles.sortSelect(isMobile))}
          value={sortOrder}
          aria-label={String(t('sort-by'))}
          onChange={(e): void => {
            const next = SORT_OPTIONS.find((option) => option.value === e.target.value);
            if (next != null) {
              setSortOrder(next.value);
              setPage(0);
            }
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>

        <div className={style(listStyles.viewToggle)} role="group" aria-label={String(t('view'))}>
          <button
            type="button"
            className={style(listStyles.viewToggleButton(view === 'list', true))}
            aria-pressed={view === 'list'}
            aria-label={String(t('list-view'))}
            onClick={(): void => setView('list')}
          >
            <FormatListBulletedIcon size={18} />
          </button>
          <button
            type="button"
            className={style(listStyles.viewToggleButton(view === 'grid', false))}
            aria-pressed={view === 'grid'}
            aria-label={String(t('grid-view'))}
            onClick={(): void => setView('grid')}
          >
            <ViewGridOutlineIcon size={18} />
          </button>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            className={style(surfaceStyles.ghostButton)}
            onClick={clearFilters}
            title={String(t('clear-filters'))}
          >
            <FilterRemoveOutlineIcon size={16} />
            {t('clear-filters')}
          </button>
        )}
      </div>
    );
  };

  const renderContent = (): React.JSX.Element => {
    if (showSpinner) {
      return (
        <div className={style(listStyles.loadingPad)}>
          <Center>
            <ActivityIndicator
              size={20}
              color={StyleConstants.GREY_COLOR}
            />
          </Center>
        </div>
      );
    }

    if (filteredParkings.length === 0) {
      const isEmpty = sortedParkings.length === 0;
      return (
        <div className={style(surfaceStyles.emptyState)}>
          <ParkingIcon size={28} />
          <span className={style(surfaceStyles.emptyTitle)}>
            {isEmpty ? t('no-parkings-available') : t('no-results-found')}
          </span>
          <span className={style(surfaceStyles.emptyHint)}>
            {isEmpty
              ? t('no-parkings-available-hint')
              : t('no-parking-results-hint')}
          </span>
        </div>
      );
    }

    const cards = paginatedParkings.map((parkingItem) => (
      <ParkingCard
        key={`${parkingItem.name} ${parkingItem.id ?? ''}`}
        initialParking={parkingItem}
        allOffices={allOffices ?? []}
        variant={view === 'grid' ? 'grid' : 'row'}
        handleChange={shouldUpdateParking}
        parkingIsValid={parkingIsValid}
        onDelete={(parking): void => {
          shouldDeleteParking(parking);
        }}
      />
    ));

    if (view === 'grid') {
      return <div className={style(listStyles.cardGrid(isMobile))}>{cards}</div>;
    }

    return <div>{cards}</div>;
  };

  return (
    <section className={style(surfaceStyles.card)}>
      {renderSectionHead()}
      {renderToolbar()}
      {renderContent()}
      {!showSpinner && filteredParkings.length > 0 && (
        <div className={style(listStyles.sectionFoot)}>
          <ListPager
            page={currentPage}
            pageSize={ITEMS_PER_PAGE}
            total={filteredParkings.length}
            onChangePage={setPage}
          />
        </div>
      )}

      <CreateParkingModal
        showModal={showCreateParkingModal}
        handleClose={handleClose}
        allOffices={allOffices ?? []}
        handleCreate={(parking): void => {
          shouldCreateParking(parking);
        }}
        isParkingValid={parkingIsValid}
      />
    </section>
  );
};

export default OfficeParkingsGroup;
